/**
 * Credit Block History React Query hooks
 * Uses Gobodo Graph Traversal API (Client INCOMING → Unlock_Request) when enabled.
 * Paginated “all requests” list uses traversal only (no bulk Unlock_Request list merge).
 * Per-client history uses Client → Unlock_Request traversal only (no bulk list + filter).
 * When USE_GOBODO_API is false, uses client.history from the client object.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { CreditBlockClient } from '../../types';
import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig from '../../config/gobodoConfig';
import { getUnlockRequestClientRelKey } from '../../config/gobodoAccountRelationshipKeys';
import { CREDIT_BLOCK_BULK_LIST_SIZE, UNLOCK_REQUEST_CACHE_OPTS } from './creditBlockQueries';
import { getRecordsForObject } from '../recordStorageService';
import { convertFromGobodoRecord } from '../gobodo/gobodoRecordService';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import type { CreditBlockHistoryEntry } from '../../types';
import { resolveGraphUserNamesBySfIds, looksLikeGraphUserSfId } from '../gobodo/gobodoUserSfIdLookup';

const CUSTOM_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
const HISTORY_OBJECT = `Unlock_Request${CUSTOM_SUFFIX}`;
const CLIENT_OBJECT = `Client${CUSTOM_SUFFIX}`;
const CLIENT_NAME_LOOKUP_TIMEOUT_MS = 4500;
const MAX_CLIENT_NAME_LOOKUPS = 40;
const HISTORY_TRAVERSAL_TIMEOUT_MS = 12_000;

export type UnlockRequestTypeFilter =
  | 'all'
  | 'lock_request'
  | 'unlock_request'
  | 'permanent_block'
  | 'permanent_unblock'
  | 'extension'
  | 'special_case';

const FILTER_TO_REQUEST_TYPE_PICKLIST: Record<Exclude<UnlockRequestTypeFilter, 'all'>, string> = {
  lock_request: 'Block',
  unlock_request: 'Unblock',
  permanent_block: 'Permanent Block',
  permanent_unblock: 'Permanent Unblock',
  extension: 'Extension',
  special_case: 'Special Case',
};

function defaultLabelForHistoryType(t: CreditBlockHistoryEntry['type']): string {
  switch (t) {
    case 'lock_request':
      return 'Block';
    case 'unlock_request':
      return 'Unblock';
    case 'permanent_block':
      return 'Permanent Block';
    case 'permanent_unblock':
      return 'Permanent Unblock';
    case 'extension':
      return 'Extension';
    case 'special_case':
      return 'Special Case';
    default:
      return 'Request';
  }
}

export type UnlockRequestSortOrder = 'newest' | 'oldest';

/** Gobodo server-side cache for unlock-request list traversal (aligns with Credit Block client list ~90s). */
const UNLOCK_REQUEST_TRAVERSAL_CACHE_DURATION_SECONDS = 90;

function unlockRequestsTraversalCacheOpts(
  startIndex: number,
  count: number,
  requestTypeFilter: UnlockRequestTypeFilter
): { cachekey: string[]; cacheduration: string } {
  return {
    cachekey: ['credit-block-unlock-requests-traversal', String(startIndex), String(count), requestTypeFilter],
    cacheduration: String(UNLOCK_REQUEST_TRAVERSAL_CACHE_DURATION_SECONDS),
  };
}
const clientIdFields = [
  `ClientRelField${CUSTOM_SUFFIX}`,
  'ClientRelField__c',
  'Client__c',
  'AccountRelField__c',
  'accountId',
  'Credit_Block_Client__c',
  'custom__credit_block_client',
];

function isMeaningfulToken(value: unknown): boolean {
  const s = String(value ?? '').trim();
  if (!s) return false;
  const low = s.toLowerCase();
  return low !== '-' && low !== '—' && low !== 'null' && low !== 'undefined' && low !== 'n/a';
}

function extractClientIdFromValue(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === 'object' && v !== null && 'relatedObject' in v) {
    const ro = (v as { relatedObject?: { id?: unknown; key?: unknown } }).relatedObject;
    if (isMeaningfulToken(ro?.key)) return String(ro?.key).trim();
    if (isMeaningfulToken(ro?.id)) return String(ro?.id).trim();
  }
  const s = String(v).trim();
  return isMeaningfulToken(s) ? s : null;
}

const getClientId = (r: Record<string, unknown>): string => {
  const data = (r.data as Record<string, unknown>) ?? r;
  const related = (r as { relatedClientId?: unknown }).relatedClientId;
  if (isMeaningfulToken(related)) {
    const v = extractClientIdFromValue(related);
    if (v) return v;
  }
  for (const f of clientIdFields) {
    const v = extractClientIdFromValue(data[f] ?? r[f]);
    if (v) return v;
  }
  return '';
};

const stripSuffix = (s: string) => s.replace(/__stg3_c$|__stg_c$|__dev_c$|__stg$|__c$/i, '');

function normalizeIdToken(raw: unknown): string {
  return stripSuffix(String(raw ?? '').trim()).toLowerCase();
}

/** Infer type from record. Request_Type__c takes precedence: "Block"=lock, "Unblock"/"Unlock"=unlock. Name (UNLOCK-00026734) is a generic auto-number for all request types. */
const normalizeType = (r: Record<string, unknown>): CreditBlockHistoryEntry['type'] => {
  const data = (r.data as Record<string, unknown>) ?? r;
  const requestType = String(
    r.Request_Type__c ?? data.Request_Type__c ??
    r.Type__c ?? data.Type__c ?? r.type ?? data.type ??
    (r as Record<string, unknown>).custom__request_type ?? (data as Record<string, unknown>)?.custom__request_type ?? ''
  ).toLowerCase().trim();
  if (requestType) {
    if (requestType.includes('extension')) return 'extension';
    if (requestType.includes('special')) return 'special_case';
    if (
      requestType.includes('permanent') &&
      (requestType.includes('unblock') || requestType.includes('unlock'))
    )
      return 'permanent_unblock';
    if (requestType.includes('permanent')) return 'permanent_block';
    if (requestType.includes('unblock') || requestType.includes('unlock')) return 'unlock_request';
    if (requestType === 'block' || (requestType.includes('block') && !requestType.includes('unblock')))
      return 'lock_request';
  }
  const combined = [
    requestType,
    r.Name__c ?? data.Name__c ?? r.name ?? data.name,
    r.Description__c ?? data.Description__c ?? r.description ?? data.description,
  ]
    .filter(Boolean)
    .map((v) => String(v))
    .join(' ')
    .toLowerCase();
  if (combined.includes('extension')) return 'extension';
  if (combined.includes('special')) return 'special_case';
  if (
    combined.includes('permanent') &&
    (combined.includes('unblock') || combined.includes('unlock'))
  )
    return 'permanent_unblock';
  if (combined.includes('permanent')) return 'permanent_block';
  if (combined.includes('unlock_request') || combined.includes('unlock')) return 'unlock_request';
  if (combined.includes('lock_request') || combined.includes('lock') || combined.includes('block')) return 'lock_request';
  if (combined.includes('subscription')) return 'subscription_change';
  if (combined.includes('product')) return 'product_change';
  if (combined.includes('account')) return 'account_update';
  return 'subscription_change';
};

const normalizeStatus = (val: unknown): CreditBlockHistoryEntry['status'] => {
  const s = String(val ?? '').toLowerCase().trim();
  if (['pending', 'approved', 'denied', 'completed', 'failed'].includes(s))
    return s as CreditBlockHistoryEntry['status'];
  if (['new', 'open', 'in progress', 'in_progress', 'processing', 'submitted'].includes(s)) return 'pending';
  if (['rejected', 'cancelled', 'canceled'].includes(s)) return 'denied';
  if (['done', 'processed', 'fulfilled'].includes(s)) return 'completed';
  return 'pending';
};

const normalizeBureau = (
  val: unknown
): CreditBlockHistoryEntry['bureau'] | undefined => {
  const s = String(val ?? '').toLowerCase();
  if (s.includes('experian')) return 'experian';
  if (s.includes('equifax')) return 'equifax';
  if (s.includes('transunion') || s.includes('trans union')) return 'transunion';
  if (s.includes('chexsystems') || s.includes('chex')) return 'chexsystems';
  if (s.includes('all')) return 'all';
  return undefined;
};

const extractDateToken = (text: unknown): string | undefined => {
  const s = String(text ?? '');
  if (!s) return undefined;
  const m = s.match(/\b(20\d{2}-\d{2}-\d{2})\b/);
  return m?.[1];
};

const normalizeDateValue = (value: unknown): string | undefined => {
  const raw = String(value ?? '').trim();
  if (!raw) return undefined;
  if (/^\d{10,13}$/.test(raw)) {
    const n = Number(raw);
    const ms = raw.length === 10 ? n * 1000 : n;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10);
  }
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10);
};

const mapToHistoryEntry = (r: Record<string, unknown>): CreditBlockHistoryEntry => {
  const data = (r.data as Record<string, unknown>) ?? r;
  const type = normalizeType(r);
  const requestTypeLabel = String(
    r.Request_Type__c ??
      data.Request_Type__c ??
      (r as Record<string, unknown>).custom__request_type ??
      defaultLabelForHistoryType(type)
  ).trim() || defaultLabelForHistoryType(type);
  const namePart = r.Name__c ?? data.Name__c ?? r.name ?? data.name ?? '';
  const desc = r.Description__c ?? data.Description__c ?? r.Reason__c ?? data.Reason__c ?? r.description ?? data.description;
  const description = desc
    ? String(desc)
    : `${requestTypeLabel} request${namePart ? ` - ${namePart}` : ''}`;
  const derivedEndDate =
    extractDateToken(
      String(r.Reason__c ?? data.Reason__c ?? '')
        .split('·')
        .find((part) => /end\s*date|end:/i.test(part))
    ) ||
    extractDateToken(description);
  const referenceNumber = String(
    r.Name__c ?? data.Name__c ?? r.Name ?? data.Name ?? r.requestNumber ?? data.requestNumber ?? ''
  ).trim();
  return {
    id: String(r.id ?? r.key ?? `hist-${Date.now()}`),
    referenceNumber: referenceNumber || undefined,
    type,
    description,
    status: normalizeStatus(
      r.Status__c ?? data.Status__c ?? r.status ?? data.status ?? (r as Record<string, unknown>).custom__status ?? (data as Record<string, unknown>)?.custom__status
    ),
    requestedBy: String(
      r.Requested_By__c ?? data.Requested_By__c ??
        r.RequestedBy__c ?? data.RequestedBy__c ??
        r.requestedBy ?? r.CreatedBy__c ?? data.CreatedBy__c ??
        r.createdBy ?? r.created_by_id ??
        ''
    ),
    requestDate: String(
      r.Request_Date__c ?? data.Request_Date__c ??
        r.RequestDate__c ?? data.RequestDate__c ??
        r.requestDate ?? r.CreatedDate__c ?? data.CreatedDate__c ??
        r.createdDate ?? r.created_date ??
        new Date().toISOString()
    ),
    processedBy: String(
      r.Processed_By__c ?? data.Processed_By__c ?? r.ProcessedBy__c ?? r.processedBy ?? ''
    ).trim() || undefined,
    processedDate: String(
      r.Processed_Date__c ?? data.Processed_Date__c ?? r.ProcessedDate__c ?? r.processedDate ?? ''
    )
      .trim()
      .slice(0, 10) || undefined,
    endDate: normalizeDateValue(
      r.Unlock_End_Date__c ??
        data.Unlock_End_Date__c ??
        r.End_Date__c ??
        data.End_Date__c ??
        r.UnlockEndDate__c ??
        data.UnlockEndDate__c ??
        r.endDate ??
        data.endDate ??
        r.end_date ??
        data.end_date ??
        r.custom__unlock_end_date ??
        (data as Record<string, unknown>)?.custom__unlock_end_date ??
        r.custom__end_date ??
        (data as Record<string, unknown>)?.custom__end_date ??
        derivedEndDate
    ),
    startDate: normalizeDateValue(
      r.Unlock_Start_Date__c ??
        data.Unlock_Start_Date__c ??
        r.Start_Date__c ??
        data.Start_Date__c ??
        r.UnlockStartDate__c ??
        data.UnlockStartDate__c ??
        r.startDate ??
        data.startDate ??
        r.start_date ??
        data.start_date ??
        r.custom__unlock_start_date ??
        (data as Record<string, unknown>)?.custom__unlock_start_date ??
        r.custom__start_date ??
        (data as Record<string, unknown>)?.custom__start_date
    ),
    bureau: normalizeBureau(r.Bureau_Name__c ?? data.Bureau_Name__c ?? r.Bureau__c ?? data.Bureau__c ?? r.bureau ?? r.custom__bureau ?? r.custom__bureau_new ?? (data as Record<string, unknown>)?.custom__bureau_new),
  };
};

/** Fetch history for one client via traversal (Client INCOMING → Unlock_Request). clientId is the client's object key. */
async function getHistoryViaTraversal(clientId: string): Promise<CreditBlockHistoryEntry[]> {
  const raw = String(clientId ?? '').trim();
  if (!raw) return [];
  const apiClient = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);
  const candidates = Array.from(
    new Set(
      [
        raw,
        toClientGobodoObjectKey(raw),
        stripSuffix(raw),
        toClientGobodoObjectKey(stripSuffix(raw)),
      ]
        .map((v) => String(v ?? '').trim())
        .filter(Boolean)
    )
  );

  const traverseByStartKey = async (startObjectKey: string): Promise<CreditBlockHistoryEntry[]> => {
    const input = new GobodoApi.GraphTraversalInput();
    input.setGraphId(GobodoConfig.graph());
    input.setStartObjectKey(startObjectKey);
    input.setRelationshipTypes([getUnlockRequestClientRelKey()]);
    input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
    input.setEndObjectTypes([HISTORY_OBJECT]);
    input.setMaxPathLength(1);
    input.setStartIndex(0);
    input.setCount(CREDIT_BLOCK_BULK_LIST_SIZE);

    const result = await withTimeout(
      retryWithBackoff(
        () => traversalApi.traverseObjects(input),
        { maxRetries: 2, initialDelayMs: 1000 }
      ).catch(() => [] as unknown[]),
      HISTORY_TRAVERSAL_TIMEOUT_MS
    ).catch(() => [] as unknown[]);

    if (!Array.isArray(result) || result.length === 0) return [];
    const entries: CreditBlockHistoryEntry[] = [];
    for (const gobodoObj of result) {
      try {
        const rec = convertFromGobodoRecord(gobodoObj, HISTORY_OBJECT) as Record<string, unknown>;
        entries.push(mapToHistoryEntry(rec));
      } catch {
        // skip malformed record
      }
    }
    return entries;
  };

  const mergedById = new Map<string, CreditBlockHistoryEntry>();
  for (const candidate of candidates) {
    const entries = await traverseByStartKey(candidate);
    for (const entry of entries) {
      const id = String(entry.id ?? '').trim();
      if (!id) continue;
      if (!mergedById.has(id)) mergedById.set(id, entry);
    }
  }
  // Merge-in parity path from raw Unlock_Request records so partial/missing graph links
  // do not hide valid client history rows in client detail.
  const candidateSet = new Set(candidates.map((c) => normalizeIdToken(c)).filter(Boolean));
  if (candidateSet.size > 0) {
    const allRecords = await getRecordsForObject(HISTORY_OBJECT, {
      size: CREDIT_BLOCK_BULK_LIST_SIZE,
      cache: UNLOCK_REQUEST_CACHE_OPTS,
    }).catch(() => [] as Record<string, unknown>[]);
    for (const rec of allRecords) {
      const rid = getClientId(rec);
      const norm = normalizeIdToken(rid);
      if (!norm || !candidateSet.has(norm)) continue;
      const entry = mapToHistoryEntry(rec);
      const id = String(entry.id ?? '').trim();
      if (!id) continue;
      if (!mergedById.has(id)) mergedById.set(id, entry);
    }
  }

  if (mergedById.size === 0) return [];

  return Array.from(mergedById.values()).sort((a, b) => {
    const ta = Number.isFinite(Date.parse(String(a.requestDate ?? ''))) ? Date.parse(String(a.requestDate ?? '')) : 0;
    const tb = Number.isFinite(Date.parse(String(b.requestDate ?? ''))) ? Date.parse(String(b.requestDate ?? '')) : 0;
    return tb - ta;
  });
}

/**
 * Build clientId -> display name from React Query cache only (e.g. paginated Clients tab).
 * No bulk Client/Account list fetch — avoids `credit-block-client-objects` getObjectsInGraphByType.
 */
async function getClientIdToNameMap(queryClient?: QueryClient): Promise<Map<string, string>> {
  const clientIdToName = new Map<string, string>();
  const cachedClients = queryClient?.getQueryData<CreditBlockClient[]>(CREDIT_BLOCK_CLIENTS_KEY);
  if (cachedClients?.length) {
    for (const c of cachedClients) {
      if (c.id && c.accountName) addClientNameMappings(clientIdToName, c.id, c.accountName);
    }
  }
  return clientIdToName;
}

/** Build traversal input for "all" unlock requests: start from all Clients, INCOMING to Unlock_Request. Optional endObjectFieldFilters by Request_Type__c (Block/Unblock). */
function buildUnlockRequestsTraversalInput(
  startIndex: number,
  count: number,
  requestTypeFilter: UnlockRequestTypeFilter,
  sortOrder: UnlockRequestSortOrder = 'newest'
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(GobodoConfig.graph());
  input.setStartObjectTypes([CLIENT_OBJECT]);
  input.setRelationshipTypes([getUnlockRequestClientRelKey()]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
  input.setEndObjectTypes([HISTORY_OBJECT]);
  input.setMaxPathLength(1);
  input.setStartIndex(startIndex);
  input.setCount(count);

  // Server-side sort by request date (SDK 0.1.192+)
  // Type defs may lag behind runtime — cast to any for new methods
  try {
    (input as any).setSortField('Request_Date__c');
    (input as any).setSortOrder(sortOrder === 'newest' ? 'DESC' : 'ASC');
  } catch {
    // Fallback: sort client-side if server doesn't support it
  }

  // Server-side type filter
  if (requestTypeFilter !== 'all') {
    try {
      const filter = new GobodoApi.IndexFilter();
      filter.setIndexName('Request_Type__c');
      filter.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
      filter.setValues([FILTER_TO_REQUEST_TYPE_PICKLIST[requestTypeFilter]]);
      input.setEndObjectFieldFilters([filter]);
    } catch {
      // Backend may not support endObjectFieldFilters; fallback applies client-side filter
    }
  }
  return input;
}

function parseUnlockRequestsTraversalCount(raw: unknown): number {
  const obj = raw != null && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  if (obj && typeof obj.count === 'number' && !Number.isNaN(obj.count)) return obj.count;
  if (obj && obj.data != null && typeof obj.data === 'object' && typeof (obj.data as Record<string, unknown>).count === 'number') {
    return (obj.data as { count: number }).count;
  }
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  return 0;
}

/** Empty array = no rows. Non-array = error (no bulk Unlock_Request fetch). */
function requireTraverseObjectsArrayUnlock(result: unknown): unknown[] {
  if (Array.isArray(result)) return result;
  throw new Error(
    result == null
      ? 'Unlock request traversal returned no data.'
      : `Unlock request traversal returned an invalid response (${typeof result}).`
  );
}

export interface UnlockRequestsPaginatedResult {
  requests: UnlockRequestWithClient[];
  totalCount: number;
}

/**
 * Fetch one page of unlock requests via traversal (Client INCOMING → Unlock_Request).
 *
 * Uses server-side sort (setSortField/setSortOrder) and pagination (setStartIndex/setCount)
 * from Gobodo SDK 0.1.192+ to avoid fetching all rows client-side.
 * Falls back to client-side sort if server-side sort fails.
 */
export async function getUnlockRequestsViaTraversal(
  page: number,
  pageSize: number,
  requestTypeFilter: UnlockRequestTypeFilter,
  sortOrder: UnlockRequestSortOrder = 'newest',
  _queryClient?: QueryClient
): Promise<UnlockRequestsPaginatedResult> {
  const apiClient = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);

  // 1. Get total count (with filters applied)
  const countInput = buildUnlockRequestsTraversalInput(0, 1, requestTypeFilter, sortOrder);
  const countCacheOpts = unlockRequestsTraversalCacheOpts(0, 1, requestTypeFilter);
  const countRaw = await retryWithBackoff(() => traversalApi.traverseObjectsCount(countInput, countCacheOpts), {
    maxRetries: 2,
    initialDelayMs: 1000,
  });
  const totalCount = parseUnlockRequestsTraversalCount(countRaw);

  if (totalCount === 0) return { requests: [], totalCount: 0 };

  // 2. Fetch exactly one page with server-side sort + offset
  const startIndex = Math.max(0, (page - 1) * pageSize);
  const input = buildUnlockRequestsTraversalInput(startIndex, pageSize, requestTypeFilter, sortOrder);
  const traversalCacheOpts = unlockRequestsTraversalCacheOpts(startIndex, pageSize, requestTypeFilter);
  const result = await retryWithBackoff(() => traversalApi.traverseObjects(input, traversalCacheOpts), {
    maxRetries: 2,
    initialDelayMs: 1000,
  });
  const objects = requireTraverseObjectsArrayUnlock(result);

  // 3. Parse results
  const requests: UnlockRequestWithClient[] = [];
  const seenRowKeys = new Set<string>();
  for (const gobodoObj of objects) {
    try {
      const rec = convertFromGobodoRecord(gobodoObj, HISTORY_OBJECT) as Record<string, unknown>;
      const clientId = getClientId(rec);
      const entry = mapToHistoryEntry(rec) as UnlockRequestWithClient;
      entry.clientId = clientId || undefined;
      const directClientName = String(
        rec.clientName ??
          rec.ClientName__c ??
          (rec.data as Record<string, unknown> | undefined)?.ClientName__c ??
          ''
      ).trim();
      if (isMeaningfulToken(directClientName)) entry.clientName = directClientName;
      // Client-side type filter fallback (in case server filter wasn't applied)
      if (requestTypeFilter !== 'all' && entry.type !== requestTypeFilter) continue;
      const rowKey = String(entry.id ?? (rec as { key?: string }).key ?? rec.id ?? '').trim();
      if (rowKey) {
        if (seenRowKeys.has(rowKey)) continue;
        seenRowKeys.add(rowKey);
      }
      requests.push(entry);
    } catch {
      // skip malformed record
    }
  }

  const cachedClientNames = await getClientIdToNameMap(_queryClient);
  const missingLookups = Array.from(
    new Set(
      requests
        .map((entry) => String(entry.clientId ?? '').trim())
        .filter(Boolean)
        .filter((id) => !cachedClientNames.get(id) && !cachedClientNames.get(stripSuffix(id)))
    )
  );
  const resolvedClientNames = await resolveClientNamesByLookup(missingLookups);

  for (const entry of requests) {
    const clientId = String(entry.clientId ?? '').trim();
    if (!clientId) continue;
    entry.clientName =
      cachedClientNames.get(clientId) ||
      cachedClientNames.get(stripSuffix(clientId)) ||
      resolvedClientNames.get(clientId) ||
      resolvedClientNames.get(stripSuffix(clientId)) ||
      undefined;
  }

  // 4. Client-side sort fallback (if server didn't sort, results will still be in a stable order)
  requests.sort((a, b) => {
    const ta = new Date(a.requestDate).getTime();
    const tb = new Date(b.requestDate).getTime();
    const safeA = Number.isNaN(ta) ? 0 : ta;
    const safeB = Number.isNaN(tb) ? 0 : tb;
    return sortOrder === 'newest' ? safeB - safeA : safeA - safeB;
  });

  return { requests, totalCount };
}

async function getUnlockRequestsViaBulkFallback(
  page: number,
  pageSize: number,
  requestTypeFilter: UnlockRequestTypeFilter,
  sortOrder: UnlockRequestSortOrder,
  queryClient?: QueryClient
): Promise<UnlockRequestsPaginatedResult> {
  const all = await fetchAllUnlockRequests(queryClient).catch(() => [] as UnlockRequestWithClient[]);
  let filtered = all;
  if (requestTypeFilter !== 'all') {
    filtered = filtered.filter((r) => r.type === requestTypeFilter);
  }
  filtered = filtered.slice().sort((a, b) => {
    const ta = Number.isFinite(Date.parse(String(a.requestDate ?? ''))) ? Date.parse(String(a.requestDate ?? '')) : 0;
    const tb = Number.isFinite(Date.parse(String(b.requestDate ?? ''))) ? Date.parse(String(b.requestDate ?? '')) : 0;
    return sortOrder === 'newest' ? tb - ta : ta - tb;
  });
  const start = Math.max(0, (page - 1) * pageSize);
  return { requests: filtered.slice(start, start + pageSize), totalCount: filtered.length };
}

export function useCreditBlockHistory(clientId: string, enabled = true) {
  return useQuery({
    queryKey: ['credit-block', 'history', clientId],
    queryFn: async (): Promise<CreditBlockHistoryEntry[]> => {
      const entries = await getHistoryViaTraversal(clientId).catch(() => [] as CreditBlockHistoryEntry[]);

      // Resolve SF user IDs to display names in requestedBy / processedBy
      const sfIds = new Set<string>();
      for (const e of entries) {
        if (looksLikeGraphUserSfId(e.requestedBy)) sfIds.add(e.requestedBy);
        if (e.processedBy && looksLikeGraphUserSfId(e.processedBy)) sfIds.add(e.processedBy);
      }
      if (sfIds.size > 0) {
        try {
          const nameMap = await resolveGraphUserNamesBySfIds(Array.from(sfIds));
          for (const e of entries) {
            const reqName = nameMap.get(e.requestedBy);
            if (reqName) e.requestedBy = reqName;
            if (e.processedBy) {
              const procName = nameMap.get(e.processedBy);
              if (procName) e.processedBy = procName;
            }
          }
        } catch {
          // Silently fall back to raw IDs
        }
      }

      return entries;
    },
    enabled: USE_GOBODO_API && !!clientId && enabled,
    staleTime: 60_000,
  });
}

export interface UnlockRequestWithClient extends CreditBlockHistoryEntry {
  clientId?: string;
  clientName?: string;
}

function addClientNameMappings(map: Map<string, string>, idVal: string, name: string) {
  if (!idVal || !name) return;
  const s = String(idVal).trim();
  if (!s) return;
  map.set(s, name);
  map.set(stripSuffix(s), name);
}

function toClientGobodoObjectKey(raw: string): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  if (s.toLowerCase().endsWith(CUSTOM_SUFFIX.toLowerCase())) return s;
  if (/(?:__stg3_c|__stg_c|__dev_c|__stg$|__c)$/i.test(s)) return s;
  return `${s}${CUSTOM_SUFFIX}`;
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('Client lookup timed out')), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timer) clearTimeout(timer);
  }) as Promise<T>;
}

function extractClientNameFromGobodoObject(rawObject: unknown): string {
  if (!rawObject || typeof rawObject !== 'object') return '';
  const obj = rawObject as Record<string, unknown>;
  const objectTypeKey = String(
    obj.objecttypekey ?? (obj.objecttype as { key?: string } | undefined)?.key ?? CLIENT_OBJECT
  );
  const rec = convertFromGobodoRecord(obj, objectTypeKey) as Record<string, unknown>;
  const data = (rec.data as Record<string, unknown>) ?? rec;
  return String(
    rec.AccountName__c ??
      data.AccountName__c ??
      rec.Name__c ??
      data.Name__c ??
      rec.name ??
      data.name ??
      ''
  ).trim();
}

async function resolveClientNamesByLookup(lookups: string[]): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  if (lookups.length === 0) return out;
  const objectsApi = new GobodoApi.GraphObjectsApi(getGobodoClient());
  const uniqueLookups = Array.from(new Set(lookups.map((v) => String(v ?? '').trim()).filter(Boolean)));
  const getStatus = (e: unknown) =>
    (e as { response?: { status?: number }; status?: number })?.response?.status ??
    (e as { status?: number })?.status;

  await Promise.all(
    uniqueLookups.map(async (lookup) => {
      const candidates = /^\d+$/.test(lookup)
        ? [lookup]
        : (() => {
            const withSuffix = toClientGobodoObjectKey(lookup);
            return withSuffix === lookup ? [lookup] : [withSuffix, lookup];
          })();

      for (const candidate of candidates) {
        try {
          const rawObject = /^\d+$/.test(candidate)
            ? await withTimeout(
                retryWithBackoff(() => objectsApi.getObjectById(Number(candidate)), { maxRetries: 1 }),
                CLIENT_NAME_LOOKUP_TIMEOUT_MS
              )
            : await withTimeout(
                retryWithBackoff(() => objectsApi.getObjectByKey(candidate), { maxRetries: 1 }),
                CLIENT_NAME_LOOKUP_TIMEOUT_MS
              );
          const name = extractClientNameFromGobodoObject(rawObject);
          if (name) {
            addClientNameMappings(out, lookup, name);
            addClientNameMappings(out, candidate, name);
            break;
          }
        } catch (error) {
          if (getStatus(error) === 404) continue;
          break;
        }
      }
    })
  );
  return out;
}

const CREDIT_BLOCK_CLIENTS_KEY = ['credit-block', 'clients', CUSTOM_SUFFIX] as const;

/** Fetch all unlock requests for dashboard. Reuses cached clients when available to avoid duplicate fetch. */
export async function fetchAllUnlockRequests(queryClient?: QueryClient): Promise<UnlockRequestWithClient[]> {
  const clientIdToName = await getClientIdToNameMap(queryClient);
  const records = await getRecordsForObject(HISTORY_OBJECT, {
    size: CREDIT_BLOCK_BULK_LIST_SIZE,
    cache: UNLOCK_REQUEST_CACHE_OPTS,
  }).catch(() => [] as Record<string, unknown>[]);
  const entries: UnlockRequestWithClient[] = (records as Record<string, unknown>[]).map((r) => {
    const clientId = getClientId(r);
    const entry = mapToHistoryEntry(r) as UnlockRequestWithClient;
    entry.clientId = clientId || undefined;
    entry.clientName = clientId ? (clientIdToName.get(clientId) ?? clientIdToName.get(stripSuffix(clientId))) : undefined;
    return entry;
  });
  const missingLookups = Array.from(
    new Set(
      entries
        .map((entry) => String(entry.clientId ?? '').trim())
        .filter((id) => isMeaningfulToken(id))
        .filter((id) => !isMeaningfulToken(clientIdToName.get(id)) && !isMeaningfulToken(clientIdToName.get(stripSuffix(id))))
    )
  ).slice(0, MAX_CLIENT_NAME_LOOKUPS);
  const resolvedClientNames = await resolveClientNamesByLookup(missingLookups);
  for (const entry of entries) {
    const clientId = String(entry.clientId ?? '').trim();
    if (!isMeaningfulToken(clientId)) continue;
    entry.clientName =
      entry.clientName ??
      clientIdToName.get(clientId) ??
      clientIdToName.get(stripSuffix(clientId)) ??
      resolvedClientNames.get(clientId) ??
      resolvedClientNames.get(stripSuffix(clientId)) ??
      undefined;
  }
  return entries.sort((a, b) => new Date(b.requestDate).getTime() - new Date(a.requestDate).getTime());
}

const isRetryableError = (err: unknown): boolean => {
  const status = (err as { response?: { status?: number }; status?: number })?.response?.status
    ?? (err as { status?: number })?.status;
  if (status === 504 || (status && status >= 500)) return false;
  return true;
};

export function useCreditBlockUnlockRequests(enabled = true) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['credit-block', 'unlock-requests', CUSTOM_SUFFIX],
    queryFn: () => fetchAllUnlockRequests(queryClient),
    enabled: USE_GOBODO_API && enabled,
    staleTime: 60_000,
    retry: (failureCount, error) => failureCount < 1 && isRetryableError(error),
  });
}

/** Paginated unlock requests with type filter (all / lock_request / unlock_request). Used by Lock/Unlock Requests tab. */
export function useCreditBlockUnlockRequestsPaginated(
  page: number,
  pageSize: number,
  requestTypeFilter: UnlockRequestTypeFilter,
  sortOrder: UnlockRequestSortOrder,
  enabled = true
) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['credit-block', 'unlock-requests', 'paginated', CUSTOM_SUFFIX, page, pageSize, requestTypeFilter, sortOrder],
    queryFn: async () => {
      try {
        const result = await getUnlockRequestsViaTraversal(page, pageSize, requestTypeFilter, sortOrder, queryClient);
        if (result.totalCount === 0 && result.requests.length === 0) {
          return getUnlockRequestsViaBulkFallback(page, pageSize, requestTypeFilter, sortOrder, queryClient);
        }
        return result;
      } catch {
        return getUnlockRequestsViaBulkFallback(page, pageSize, requestTypeFilter, sortOrder, queryClient);
      }
    },
    enabled: USE_GOBODO_API && enabled && page >= 1 && pageSize >= 1,
    staleTime: 60_000,
    retry: (failureCount, error) => failureCount < 1 && isRetryableError(error),
  });
}
